/* oxlint-disable typescript/no-explicit-any */
import type { StandardSchemaV1 } from '@standard-schema/spec';
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { create as axiosCreate } from 'axios';
import humps, { type HumpsProcessor } from 'humps';
import camelCase from 'lodash/camelCase';
import isBoolean from 'lodash/isBoolean';

import type { AxiosOptions } from '@/types/axios';
import type { HttpClient, HttpClientOptions, HttpResponse } from '@/types/http';

import { attemptTokenRefresh, isTokenErrorDescription } from '@/apis/auth-refresh';
import { IMPERSONATED_USER_ID_KEY } from '@/stores/impersonator';
import { useTokenStore } from '@/stores/token';
import { browserStorageGetSessionItem } from '@/utils/browserStorage';
import { trackRequest } from '@/utils/request-activity';
import { standardValidate } from '@/utils/standardValidate';

type AuthRetryRequestConfig = InternalAxiosRequestConfig & {
  _hasRetriedAfterAuthRefresh?: boolean;
};

function normalizeResponse<T>(response: AxiosResponse<T>): HttpResponse<T> {
  return {
    data: response.data,
    status: response.status,
    headers: Object.fromEntries(Object.entries(response.headers)),
  };
}

// humps walks own keys, so a Temporal value (internal slots only) would go out as `{}`.
function stringifyTemporalValues(value: unknown): unknown {
  if (Object.prototype.toString.call(value).startsWith('[object Temporal.')) return String(value);
  if (Array.isArray(value)) return value.map(stringifyTemporalValues);
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, stringifyTemporalValues(entry)]),
    );
  }
  return value;
}

function buildHttpClient(getAx: () => AxiosInstance): HttpClient {
  const send = <T>(request: Promise<AxiosResponse<T>>) =>
    trackRequest(request).then(normalizeResponse);
  return {
    get: (url, config) => send(getAx().get(url, config)),
    delete: (url, config) => send(getAx().delete(url, config)),
    head: (url, config) => send(getAx().head(url, config)),
    options: (url, config) => send(getAx().options(url, config)),
    post: (url, data, config) => send(getAx().post(url, data, config)),
    put: (url, data, config) => send(getAx().put(url, data, config)),
    patch: (url, data, config) => send(getAx().patch(url, data, config)),
  };
}

async function attemptTokenRefreshIfBadToken(error: AxiosError) {
  const { status, data = {} as any } = error.response ?? {};
  // Checking if status is 401 should be enough to determine if we need to redirect to login,
  // but just in case, we check the error message to ensure we're only redirecting when token
  // is invalid or expired.
  if (status === 401 && isTokenErrorDescription(data.description)) {
    return attemptTokenRefresh();
  }
  return false;
}

function createAxios(
  baseURL: string,
  {
    includeAuth = true,
    camelizeKeys = false,
    decamelizeRequests = true,
    decamelizeIgnoreKeys,
  }: AxiosOptions = {},
) {
  const ax = axiosCreate({ baseURL });

  ax.interceptors.request.use((axConfig: InternalAxiosRequestConfig & AxiosOptions) => {
    // Catch object-interpolated path segments (e.g. `/dashboards/${someObj}`)
    // before the request ships, so Datadog surfaces a useful stack trace
    // instead of the eventual `Unexpected token '<'` from JSON.parse on the
    // SPA shell HTML that the server returns for unmatched API routes.
    if (axConfig.url?.includes('[object Object]')) {
      throw new Error(
        `Refusing to send request with object-interpolated URL: ${axConfig.url}. ` +
          `Pass a primitive identifier (e.g. an id) into the URL template.`,
      );
    }
    const decamelizeKeys = isBoolean(axConfig.decamelizeRequests)
      ? axConfig.decamelizeRequests
      : decamelizeRequests;
    if (decamelizeKeys) {
      const decamelizeConvert: HumpsProcessor = (key, convert, convertOptions) =>
        key !== camelCase(key) || decamelizeIgnoreKeys?.includes(key)
          ? key
          : convert(key, convertOptions);
      axConfig.params = humps.decamelizeKeys(
        stringifyTemporalValues(axConfig.params) as object,
        decamelizeConvert,
      );
      axConfig.data = humps.decamelizeKeys(
        stringifyTemporalValues(axConfig.data) as object,
        decamelizeConvert,
      );
    }
    if (includeAuth) {
      const token = useTokenStore().token;
      if (token && !axConfig.headers.Authorization) {
        axConfig.headers.Authorization = `Bearer ${token}`;
      }
    }
    const impersonatedUserId = browserStorageGetSessionItem(IMPERSONATED_USER_ID_KEY);
    if (impersonatedUserId) {
      axConfig.headers['X-On-Behalf-Of'] = impersonatedUserId;
    }
    return axConfig;
  });

  ax.interceptors.response.use(
    async (response) => {
      const requestConfig = response?.config as Pick<AxiosOptions, 'camelizeKeys'> & {
        schema?: StandardSchemaV1;
      };
      const shouldCamelizeKeys = isBoolean(requestConfig?.camelizeKeys)
        ? requestConfig?.camelizeKeys
        : camelizeKeys;
      const data = requestConfig?.schema
        ? await standardValidate(requestConfig.schema, response.data)
        : response.data;
      return {
        ...response,
        data: shouldCamelizeKeys ? humps.camelizeKeys(data) : data,
      };
    },
    async (error) => {
      const tokenStore = useTokenStore();
      if (tokenStore.token && !tokenStore.isShareToken && error.response) {
        const originalConfig = error.response.config as AuthRetryRequestConfig;
        if (originalConfig._hasRetriedAfterAuthRefresh) {
          return Promise.reject(error);
        }

        // we do const { status, data } = error.response.
        // want to make sure error.response exists.
        const success = await attemptTokenRefreshIfBadToken(error);
        if (success) {
          // if token was refreshed successfully, retry the request
          originalConfig._hasRetriedAfterAuthRefresh = true;
          originalConfig.headers.Authorization = `Bearer ${tokenStore.token}`;
          return ax(originalConfig);
        }
      }
      return Promise.reject(error);
    },
  );

  return ax;
}

/**
 * Creates an HTTP client for internal APIs with authentication, impersonation, and camelization.
 *
 * For external APIs, use createHttpClient from http.ts instead.
 */
export function createAxiosInstance(baseURL: string, options?: HttpClientOptions): HttpClient {
  let ax: AxiosInstance | null = null;
  const getAx = () => {
    ax ??= createAxios(baseURL, options);
    return ax;
  };
  return buildHttpClient(getAx);
}
